import { useState } from "react";
import { C, Card, CardHead, SectionTitle, Btn, Badge, Modal, Field, SshCopyBlock } from "./theme";
import { GET, POST, DELETE } from "./api";
import { useLang } from "./i18n";

export default function UserSSH({ me, vms, collabVms, sshKeys, getServerIp, fetchAll }) {
  const { t } = useLang();
  const [showAdd, setShowAdd] = useState(false);
  const [keyName, setKeyName] = useState("");
  const [keyValue, setKeyValue] = useState("");
  const [deleting, setDeleting] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [vmKeyData, setVmKeyData] = useState({});

  const addKey = async () => {
    if (!keyName.trim() || !keyValue.trim()) return alert("이름과 키를 입력하세요");
    setSubmitting(true);
    try {
      await POST(`/api/users/${me.id}/ssh-keys`, { name: keyName.trim(), key: keyValue.trim() });
      await fetchAll();
      setShowAdd(false);
      setKeyName("");
      setKeyValue("");
    } catch (e) { alert(e.message); }
    setSubmitting(false);
  };

  const removeKey = async (keyId) => {
    if (!window.confirm("이 공개키를 삭제하시겠습니까?")) return;
    setDeleting(p => ({ ...p, [keyId]: true }));
    try {
      await DELETE(`/api/users/${me.id}/ssh-keys/${keyId}`);
      await fetchAll();
    } catch (e) { alert(e.message); }
    setDeleting(p => ({ ...p, [keyId]: false }));
  };

  const downloadVmKey = async (vmId) => {
    try {
      const token = localStorage.getItem("gpu_fabric_token");
      const res = await fetch(`/api/vms/${vmId}/key`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) { alert("키 다운로드 실패"); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${vmId}.pem`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { alert(e.message); }
  };

  const viewVmKey = async (vmId) => {
    if (vmKeyData[vmId]) { setVmKeyData(p => ({ ...p, [vmId]: null })); return; }
    try {
      const token = localStorage.getItem("gpu_fabric_token");
      const res = await fetch(`/api/vms/${vmId}/key`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) { alert("키 조회 실패"); return; }
      const text = await res.text();
      setVmKeyData(p => ({ ...p, [vmId]: text }));
    } catch (e) { alert(e.message); }
  };

  const allVms = [
    ...(vms || []).map(v => ({ ...v, _owned: true })),
    ...(collabVms || []).map(v => ({ ...v, _owned: false })),
  ];

  // 소유 VM: 전용 키(vm.id.pem)로 접속. 협업 VM: 협업자는 전용 키를 받을 권한이 없고
  // (백엔드 _check_vm_access 가 403), 본인이 등록한 공개키로 접속하는 설계 → -i 안내 금지.
  const sshCmd = (vm, ip) => vm._owned
    ? `ssh -i ${vm.id}.pem -p ${vm.ssh_port} ${me.id}@${ip}`
    : `ssh -p ${vm.ssh_port} ${me.id}@${ip}`;
  const sshConfig = (vm, ip) => vm._owned
    ? `Host ${vm.id}\n  HostName ${ip}\n  User ${me.id}\n  Port ${vm.ssh_port}\n  IdentityFile ~/.ssh/${vm.id}.pem`
    : `Host ${vm.id}\n  HostName ${ip}\n  User ${me.id}\n  Port ${vm.ssh_port}`;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* SSH Access per VM */}
      <SectionTitle title={t("ssh_access_list")} />
      {allVms.length === 0 ? (
        <div style={{ fontSize: 12, color: C.dim2, fontFamily: "monospace", textAlign: "center", padding: 20 }}>
          접속 가능한 VM이 없습니다
        </div>
      ) : (
        allVms.map(vm => {
          const ip = getServerIp(vm.server);
          return (
            <Card key={vm.id}>
              <CardHead>
                <span style={{ fontSize: 12, fontFamily: "monospace", color: C.text, fontWeight: 700 }}>
                  {vm.id}
                </span>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <Badge label={vm.server} color={C.blue} />
                  {vm._owned ? (
                    <>
                      <Btn small variant="cyan" onClick={() => downloadVmKey(vm.id)}>{t("key_download")}</Btn>
                      <Btn small variant="ghost" onClick={() => viewVmKey(vm.id)}>
                        {vmKeyData[vm.id] ? t("hide") : t("key_view")}
                      </Btn>
                    </>
                  ) : (
                    <Badge label={`협업 · ${vm.owner_name || vm.user_id}`} color={C.purple} />
                  )}
                </div>
              </CardHead>
              <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: 6 }}>
                <SshCopyBlock label={t("ssh_command")} text={sshCmd(vm, ip)} />
                <SshCopyBlock label={t("ssh_config")} text={sshConfig(vm, ip)} />
                {vmKeyData[vm.id] && (
                  <div style={{ position: "relative" }}>
                    <pre style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6,
                      padding: 10, fontSize: 10, color: C.cyan, fontFamily: "monospace",
                      whiteSpace: "pre-wrap", wordBreak: "break-all", maxHeight: 160, overflow: "auto" }}>
                      {vmKeyData[vm.id]}
                    </pre>
                    <button onClick={() => { navigator.clipboard?.writeText(vmKeyData[vm.id]); }}
                      style={{ position: "absolute", top: 8, right: 8, background: "none", border: "none",
                        color: C.dim2, cursor: "pointer", fontSize: 10, fontFamily: "monospace" }}>{t("copy")}</button>
                  </div>
                )}
                <div style={{ fontSize: 9, color: C.dim2, fontFamily: "monospace", marginTop: 4 }}>
                  {vm._owned ? t("key_guide") : "협업 VM — 본인이 등록한 공개키로 접속합니다 (전용 키 파일 없음)"}
                </div>
              </div>
            </Card>
          );
        })
      )}

      {/* SSH Key Management */}
      <SectionTitle
        title={t("my_pubkeys")}
        action={<Btn small variant="cyan" onClick={() => setShowAdd(true)}>{t("add_pubkey")}</Btn>}
      />
      <div style={{ fontSize: 10, color: C.dim2, fontFamily: "monospace", marginTop: -10, marginBottom: 6 }}>
        {t("pubkey_auto_apply")}
      </div>

      {(!sshKeys || sshKeys.length === 0) ? (
        <div style={{ fontSize: 12, color: C.dim2, fontFamily: "monospace", textAlign: "center", padding: 20 }}>
          {t("no_pubkeys")}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {sshKeys.map(k => (
            <Card key={k.id}>
              <div style={{ padding: "10px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: 12, color: C.text, fontFamily: "monospace", fontWeight: 700 }}>
                    {k.name}
                  </div>
                  <div style={{ fontSize: 10, color: C.dim2, fontFamily: "monospace", marginTop: 3 }}>
                    {k.key.length > 60 ? k.key.slice(0, 60) + "..." : k.key}
                  </div>
                  <div style={{ fontSize: 9, color: C.dim, fontFamily: "monospace", marginTop: 3 }}>
                    추가: {new Date(k.added_at + "Z").toLocaleDateString("ko-KR")}
                  </div>
                </div>
                <Btn small variant="red" disabled={deleting[k.id]} onClick={() => removeKey(k.id)}>
                  {deleting[k.id] ? "..." : t("delete")}
                </Btn>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Add Key Modal */}
      {showAdd && (
        <Modal title={t("add_pubkey")} onClose={() => setShowAdd(false)}>
          <Field label={t("key_name")} value={keyName} onChange={setKeyName} placeholder="macbook-pro" />
          <div style={{ marginBottom: 13 }}>
            <div style={{ fontSize: 9, color: C.dim2, fontFamily: "monospace", letterSpacing: 1, marginBottom: 5, textTransform: "uppercase" }}>
              {t("pubkey")}
            </div>
            <textarea
              value={keyValue}
              onChange={e => setKeyValue(e.target.value)}
              placeholder="ssh-ed25519 AAAA... user@host"
              rows={4}
              style={{
                width: "100%", background: C.panel2, border: `1px solid ${C.border2}`,
                borderRadius: 6, color: C.text, padding: "8px 10px", fontSize: 12,
                fontFamily: "monospace", outline: "none", boxSizing: "border-box", resize: "vertical"
              }}
            />
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Btn variant="ghost" onClick={() => setShowAdd(false)}>{t("cancel")}</Btn>
            <Btn variant="cyan" disabled={submitting} onClick={addKey}>
              {submitting ? "..." : t("add")}
            </Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}
